'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  LocationOpeningHours,
  OrganizationLocation,
  UpdateStandingOpeningHoursInput,
} from '@borradh-workspace/api-client/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getOpeningHoursConfirmation } from '../../lib/confirmation';

interface UseUpdateStandingOpeningHoursOptions {
  onSuccess?: (location: OrganizationLocation) => void;
  onError?: (error: Error) => void;
}

export const useUpdateStandingOpeningHours = (
  options?: UseUpdateStandingOpeningHoursOptions
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      locationId,
      openingHours,
      confirmedImpactToken,
    }: {
      locationId: string;
      openingHours: LocationOpeningHours | null;
      confirmedImpactToken?: string;
    }) =>
      apiClient.put<
        OrganizationLocation & {
          staffReviewEmailStatus?: 'sent' | 'failed' | 'not_needed';
        }
      >(`locations/${locationId}/opening-hours/standing`, {
        openingHours,
        ...(confirmedImpactToken ? { confirmedImpactToken } : {}),
      } satisfies UpdateStandingOpeningHoursInput),
    onSuccess: (location) => {
      void queryClient.invalidateQueries({
        queryKey: ['location-opening-hours'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['organization-locations'],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(),
      });
      toast.success('Opening hours updated');
      options?.onSuccess?.(location);
    },
    onError: (error: Error) => {
      if (getOpeningHoursConfirmation(error)) return;
      toast.error(error.message || 'Failed to update opening hours');
      options?.onError?.(error);
    },
  });

  return {
    updateStandingOpeningHours: mutation.mutate,
    updateStandingOpeningHoursAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
};
